import { Analytics } from '@util/Analytics';
import { translate } from '@util/Localization';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Color } from '../../../config/Color';
import { ConsentCheckbox } from '../../styles/ConsentCheckbox';
import { AuthorizationConfig, FullAuthorization } from './FullAuthorization';

/** A consent whose label is the full text, with a link to the privacy policy under it. */
export type PrivacyPolicyConsentConfig = {
  label: string;
  privacyPolicyTitle: string;
};

/**
 * The "check my coverage" variant of the insurance-provider screen, shown to the coverage audiences
 * (US non-65+, AU): new copy, required consents and self-pay escape hatches. Configured per flow so
 * the other provider screens (65+ Advantage list, legacy variants) are untouched.
 */
export type CoverageCheckLayoutConfig = {
  title: string;
  question: string;
  primaryButtonTitle: string;
  /** Required. The US shows the full authorization behind a toggle; AU links the privacy policy. */
  authorization: AuthorizationConfig | PrivacyPolicyConsentConfig;
  /** A second required consent (US only). */
  assignmentOfBenefits?: { label: string };
  /** AU: "I don't have private health insurance", a second exit shown above the self-pay one. */
  noPrivateInsuranceTitle?: string;
  selfPayTitle: string;
};

export type CoverageCheckConsentValues = {
  authorization: boolean;
  assignmentOfBenefits: boolean;
};

/** Whether every consent the config asks for is ticked. */
export function areCoverageConsentsGiven(
  config: CoverageCheckLayoutConfig,
  values: CoverageCheckConsentValues
): boolean {
  return (
    values.authorization &&
    (config.assignmentOfBenefits === undefined || values.assignmentOfBenefits)
  );
}

type Props = {
  config: CoverageCheckLayoutConfig;
  values: CoverageCheckConsentValues;
  onChange: (values: CoverageCheckConsentValues) => void;
};

export const CoverageCheckConsents: FunctionComponent<Props> = ({ config, values, onChange }) => (
  <Container>
    <ConsentCheckbox
      checked={values.authorization}
      onChange={(authorization) => onChange({ ...values, authorization })}
      label={config.authorization.label}
      testId="coverage-consent-authorization"
    >
      {'fullText' in config.authorization ? (
        <FullAuthorization config={config.authorization} testId="coverage-authorization" />
      ) : (
        <PrivacyPolicyLink
          href={translate('links.privacy')}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => Analytics.track({ event: Analytics.Event.o_privacy_opened })}
          data-testid="coverage-privacy-policy"
        >
          {config.authorization.privacyPolicyTitle}
        </PrivacyPolicyLink>
      )}
    </ConsentCheckbox>
    {config.assignmentOfBenefits !== undefined && (
      <ConsentCheckbox
        checked={values.assignmentOfBenefits}
        onChange={(assignmentOfBenefits) => onChange({ ...values, assignmentOfBenefits })}
        label={config.assignmentOfBenefits.label}
        testId="coverage-consent-assignment"
      />
    )}
  </Container>
);

const Container = styled.div`
  margin: 0 auto;
  max-width: 600px;
  text-align: left;
`;

// Styled like the full-authorization toggle so both consent variants read the same.
const PrivacyPolicyLink = styled.a`
  display: inline-block;
  margin: ${({ theme }) => `${theme.spacing._8px} 0 0 ${theme.spacing._8px}`};
  color: ${Color.content.default};
  font-size: ${({ theme }) => theme.fontSize.xs};
  text-decoration: underline;
  &:hover {
    color: ${Color.content.default};
  }
`;
